/**
 * Client-side PII screening for the WizardPane's free-text notes field.
 *
 * This is the FIRST layer of the three-layer PII defense documented in
 * ADR-007. The server's `PiiRedactor` middleware runs a more robust regex +
 * NER pass on every inbound message (layer 2); the system prompt forbids
 * the LLM from repeating or storing PII it observes despite redaction
 * (layer 3). The client-side check exists for fast feedback so the agent
 * fixes their input before the request even leaves the browser.
 *
 * Patterns are intentionally permissive — false positives are acceptable
 * (the agent can rephrase); false negatives are not (server-side picks up
 * what slips through). Match priorities order from most-specific to most-
 * general so the returned label points the agent at the right edit.
 */

const PATTERNS = [
  // SSN — dashed or 9 contiguous digits not adjacent to other digits.
  { type: 'an SSN', regex: /\b\d{3}-\d{2}-\d{4}\b/ },
  { type: 'an SSN', regex: /(?<!\d)\d{9}(?!\d)/ },

  // US phone — common formats: (xxx) xxx-xxxx, xxx-xxx-xxxx, xxx.xxx.xxxx
  { type: 'a phone number', regex: /\(\d{3}\)\s?\d{3}-\d{4}\b/ },
  { type: 'a phone number', regex: /\b\d{3}[-.\s]\d{3}[-.\s]\d{4}\b/ },

  // Email
  { type: 'an email address', regex: /\b[\w.+-]+@[\w-]+\.[\w.-]+\b/ },

  // Date of birth — common formats: MM/DD/YYYY, MM-DD-YYYY, M/D/YY, etc.
  // Permissive: any date-shaped slash/dash combo with 2-4 digit year.
  {
    type: 'a date of birth',
    regex: /\b(0?[1-9]|1[0-2])[\/\-](0?[1-9]|[12]\d|3[01])[\/\-](19|20)?\d{2}\b/,
  },

  // US ZIP code — proxy for postal address. 5 digits, optional +4.
  // Word-boundary anchored so phone-suffix digits don't false-positive.
  { type: 'a ZIP code', regex: /\b\d{5}(-\d{4})?\b/ },
]

/**
 * Returns the human-readable category of the first PII pattern that matches
 * the input (e.g. `'an SSN'`, `'a phone number'`), or `null` if nothing
 * matches. The wizard surfaces the category to the agent so they know what
 * to edit.
 *
 * @param {string} text
 * @returns {string|null}
 */
export function detectPii(text) {
  if (!text || typeof text !== 'string') return null
  for (const { type, regex } of PATTERNS) {
    if (regex.test(text)) return type
  }
  return null
}

export default detectPii
